import type { KaartInfo } from './museum';

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

function naarUtc(iso: string): number {
  const m = ISO.exec(iso);
  if (!m) throw new Error(`Ongeldige datum: ${iso}`);
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function vanUtc(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/**
 * Laatste geldige dag van een Museumkaart die op `vanaf` is ingegaan: één jaar later, min één dag.
 * Eerst een dag eraf en dan een jaar erbij, zodat 29 februari netjes op 28 februari uitkomt.
 */
export function vervalDatum(vanaf: string): string {
  const dagErvoor = new Date(naarUtc(vanaf) - 86_400_000);
  const j = dagErvoor.getUTCFullYear() + 1;
  const m = dagErvoor.getUTCMonth();
  const laatsteDag = new Date(Date.UTC(j, m + 1, 0)).getUTCDate();
  return vanUtc(Date.UTC(j, m, Math.min(dagErvoor.getUTCDate(), laatsteDag)));
}

/** Aantal dagen van `van` tot `tot` (negatief als `tot` eerder is). */
export function dagenTussen(van: string, tot: string): number {
  return Math.round((naarUtc(tot) - naarUtc(van)) / 86_400_000);
}

export function plusDagen(iso: string, dagen: number): string {
  return vanUtc(naarUtc(iso) + dagen * 86_400_000);
}

/** Vanaf zoveel dagen vóór het verlopen verschijnt de herinnering. */
export const HERINNERING_DAGEN = 30;

export type KaartStaat =
  | { soort: 'onbekend' }
  | { soort: 'geldig'; verval: string; dagen: number; binnenkort: boolean }
  | { soort: 'verlopen'; verval: string; dagenGeleden: number };

/** Hoe staat je kaart ervoor op `nu` (ISO-datum van vandaag)? */
export function kaartStaat(kaart: KaartInfo, nu: string): KaartStaat {
  if (!kaart.vanaf) return { soort: 'onbekend' };
  const verval = vervalDatum(kaart.vanaf);
  const dagen = dagenTussen(nu, verval);
  if (dagen < 0) return { soort: 'verlopen', verval, dagenGeleden: -dagen };
  return { soort: 'geldig', verval, dagen, binnenkort: dagen <= HERINNERING_DAGEN };
}

const ics = (s: string) => s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
const dag = (iso: string) => iso.replace(/-/g, '');

/**
 * Agenda-bestand (.ics) met twee hele-dag-afspraken: een herinnering 30 dagen vóór het verlopen
 * en de laatste geldige dag. Beide met een melding om 09:00. Werkt in elke agenda-app.
 */
export function maakIcs(verval: string, tekst: { herinnering: string; verlopen: string; uitleg: string }, nu = new Date()): string {
  const stempel = nu.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const afspraak = (uid: string, datum: string, titel: string) =>
    [
      'BEGIN:VEVENT',
      `UID:${uid}@museumkompas.fermaak.nl`,
      `DTSTAMP:${stempel}`,
      `DTSTART;VALUE=DATE:${dag(datum)}`,
      `DTEND;VALUE=DATE:${dag(plusDagen(datum, 1))}`,
      `SUMMARY:${ics(titel)}`,
      `DESCRIPTION:${ics(tekst.uitleg)}`,
      'BEGIN:VALARM',
      'ACTION:DISPLAY',
      `DESCRIPTION:${ics(titel)}`,
      'TRIGGER:PT9H',
      'END:VALARM',
      'END:VEVENT',
    ].join('\r\n');
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//FerMaak//Museumkompas//NL',
    'CALSCALE:GREGORIAN',
    afspraak(`herinnering-${verval}`, plusDagen(verval, -HERINNERING_DAGEN), tekst.herinnering),
    afspraak(`verval-${verval}`, verval, tekst.verlopen),
    'END:VCALENDAR',
    '',
  ].join('\r\n');
}
